import React, { useEffect, useState } from 'react';
import { QrCode, Upload, CreditCard, KeyRound, CheckCircle2, TrendingUp } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

export default function PaymentsTab() {
  const { settings, setSettings, fetchAdminSettings, refresh } = useData();
  const { toast } = useToast();
  const [local, setLocal] = useState(settings);

  useEffect(() => { fetchAdminSettings().catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { setLocal(settings); }, [settings]);

  const persist = async (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    setSettings(next);
    try {
      await api.put('/api/settings', patch);
      await refresh();
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const uploadQR = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => { persist({ qr: r.result }); toast({ title: 'QR uploaded' }); };
    r.readAsDataURL(f);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-nv-wine mb-2 flex items-center gap-2"><QrCode className="w-5 h-5" /> UPI QR Code</h3>
        <p className="text-sm text-nv-muted mb-4">Upload the UPI QR shown to devotees during checkout (fallback when Razorpay is off).</p>
        <img src={local.qr} alt="QR" className="w-56 h-56 mx-auto bg-white p-3 rounded-xl border border-orange-200 object-contain" />
        <label className="mt-5 btn-orange rounded-lg py-2.5 px-4 font-semibold flex items-center justify-center gap-2 cursor-pointer">
          <Upload className="w-4 h-4" /> Upload New QR
          <input type="file" accept="image/*" onChange={uploadQR} className="hidden" />
        </label>
      </div>

      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-nv-wine mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Razorpay Gateway</h3>
        <p className="text-sm text-nv-muted mb-4">Accept UPI, cards, netbanking & wallets. Get your keys from <a href="https://dashboard.razorpay.com/app/website-app-settings/api-keys" target="_blank" rel="noreferrer" className="text-orange-700 underline">Razorpay Dashboard</a>.</p>

        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="rzp-enabled-toggle" type="checkbox" checked={!!local.razorpayEnabled} onChange={(e) => persist({ razorpayEnabled: e.target.checked })} className="w-4 h-4" />
            Enable Razorpay checkout
          </label>

          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Mode</label>
            <div className="flex gap-2">
              {['test', 'live'].map((m) => (
                <button key={m} onClick={() => persist({ razorpayMode: m })}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${local.razorpayMode === m ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-stone-600 border-stone-300'}`}>
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Razorpay Key ID</label>
            <input data-testid="rzp-keyid" value={local.razorpayKeyId || ''} onChange={(e) => setLocal({ ...local, razorpayKeyId: e.target.value })} onBlur={(e) => persist({ razorpayKeyId: e.target.value })} placeholder={local.razorpayMode === 'test' ? 'rzp_test_XXXXXXXXXXXXX' : 'rzp_live_XXXXXXXXXXXXX'} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Razorpay Key Secret</label>
            <input type="password" data-testid="rzp-secret" value={local.razorpayKeySecret || ''} onChange={(e) => setLocal({ ...local, razorpayKeySecret: e.target.value })} onBlur={(e) => persist({ razorpayKeySecret: e.target.value })} placeholder="secret_key_here" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
            <p className="text-[10px] text-stone-500 mt-1">Secret is stored on the backend and never returned to the public settings endpoint.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">UPI ID (fallback)</label>
            <input value={local.upiId || ''} onChange={(e) => setLocal({ ...local, upiId: e.target.value })} onBlur={(e) => persist({ upiId: e.target.value })} placeholder="yourupi@bank" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          {local.razorpayEnabled && local.razorpayKeyId ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Razorpay {String(local.razorpayMode || 'test').toUpperCase()} mode configured. Checkout will use gateway.
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Enable Razorpay & add keys to activate online payment gateway.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
